import { Fragment, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarHeart, ChevronDown, PackageCheck } from "lucide-react";
import { timeLeft } from "@/lib/agenda";
import { dateLabel, refreshAgenda, timeLabel, useAgenda, yearOf } from "@/lib/agendaStore";
import { Bow, Holly, Spark } from "./Decor";

// Postal de Natal da homepage: um postal de papel marfim com pesponto, sobre um fundo de tecido vichy com laços.
// O estado (aberta, quase completa, encerrada) vem do servidor; este componente só o desenha.
const PAPER = "/img/agenda/vichy-lacos-1200.webp";
// Faixa a toda a largura: ondinhas com pesponto em cima e em baixo, como o resto da loja (azulejos --wave-up / --wave-down).
const BandFrame = ({ children }) => <div className="bb-container">{children}</div>;
const PAPER_SMALL = "/img/agenda/vichy-lacos-640.webp";

// Contagem decrescente que acompanha o relógio do servidor. Quando chega a zero, pede o estado outra vez (a agenda fecha sozinha).
function useCountdown(iso, offset = 0) {
  const calc = () => timeLeft(iso, Date.now() + offset);
  const [t, setT] = useState(calc);
  useEffect(() => {
    setT(calc());
    const id = setInterval(() => {
      const n = calc();
      setT((prev) => (prev.seconds === n.seconds && prev.minutes === n.minutes ? prev : n));
      if (n.done) {
        clearInterval(id);
        refreshAgenda();
      }
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iso, offset]);
  return t;
}

const pad = (n) => String(n).padStart(2, "0");
const plural = (n, one, many) => (n === 1 ? one : many);

// A maior unidade que ainda falta: "51 dias", "5 horas" ou "12 minutos".
const biggest = (t) => (t.days >= 1 ? [t.days, plural(t.days, "dia", "dias")] : t.hours >= 1 ? [t.hours, plural(t.hours, "hora", "horas")] : [t.minutes, plural(t.minutes, "minuto", "minutos")]);

// Carimbo do correio: o número de dias no centro e a data a rodear. Tem um ligeiro ar de carimbado à mão.
function Postmark({ ring, big, small, size = 176 }) {
  const id = useId().replace(/:/g, "");
  return (
    <div className="relative shrink-0" style={{ width: size, height: size, transform: "rotate(-7deg)" }}>
      <svg viewBox="0 0 176 176" width={size} height={size} aria-hidden="true" className="absolute inset-0 text-bordeaux">
        <defs><path id={id} d="M88 88m-66 0a66 66 0 1 1 132 0a66 66 0 1 1-132 0" /></defs>
        <circle cx="88" cy="88" r="82" fill="rgba(250,247,242,.86)" stroke="currentColor" strokeOpacity=".38" strokeWidth="1.6" />
        <circle cx="88" cy="88" r="82" fill="none" stroke="currentColor" strokeOpacity=".38" strokeWidth="1.6" />
        <circle cx="88" cy="88" r="52" fill="none" stroke="currentColor" strokeOpacity=".3" strokeWidth="1.2" strokeDasharray="2 4.2" strokeLinecap="round" />
        <text fill="currentColor" fillOpacity=".72" fontSize="11.5" fontWeight="600" letterSpacing="2.4" style={{ fontFamily: "var(--font-sans, inherit)" }}>
          <textPath href={`#${id}`} startOffset="0" textLength="404" lengthAdjust="spacing">{ring}</textPath>
        </text>
      </svg>
      <div className="absolute inset-0 grid place-content-center text-center text-bordeaux">
        <span className="font-display text-[3.1rem] leading-none tabular-nums" style={{ fontSize: size * 0.27 }}>{big}</span>
        <span className="mt-1 text-[10.5px] font-semibold uppercase tracking-[0.2em] text-bordeaux/75">{small}</span>
      </div>
    </div>
  );
}

// "Encomendas até 1 de dezembro" (e a hora, se não for ao fim do dia).
const untilText = (iso) => `${dateLabel(iso)}${["23:59", "00:00"].includes(timeLabel(iso)) ? "" : `, às ${timeLabel(iso)}`}`;

// Título com a última palavra na letra manuscrita do logótipo.
const Title = ({ text }) => {
  const words = String(text).trim().split(" ");
  const last = words.pop();
  return <>{words.length ? `${words.join(" ")} ` : ""}<span className="bb-accent text-bordeaux">{last}</span></>;
};

function Live({ iso, offset, children }) {
  const t = useCountdown(iso, offset);
  return children(t);
}

const Fact = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-2.5 text-[14px] leading-snug text-ink">
    <span className="mt-px grid h-7 w-7 shrink-0 place-items-center rounded-full bg-rose-soft text-bordeaux"><Icon className="h-3.5 w-3.5" aria-hidden="true" /></span>
    <span className="pt-0.5">{children}</span>
  </li>
);

/**
 * O postal. `agenda` tem o formato de GET /api/agenda. Em `preview` (painel) não leva a lado nenhum e abre sempre expandido.
 */
export function PostcardView({ agenda, preview = false, band = false }) {
  const [open, setOpen] = useState(preview);
  const closed = agenda.status === "closed";
  const almost = agenda.status === "almost";
  const iso = agenda.closesAt;
  const offset = agenda.clockOffset ?? 0;
  const year = iso ? yearOf(iso) : null;
  const text = closed ? agenda.closedMessage : almost ? agenda.almostMessage : agenda.message;
  const cta = preview ? "span" : Link;
  const Cta = cta;
  const ctaProps = preview ? {} : { to: "/loja" };
  const toggleId = useId();

  const Frame = band ? BandFrame : Fragment;
  return (
    <section
      aria-label="Agenda de Natal"
      data-testid="christmas-postcard"
      data-status={agenda.status}
      className={band ? "bb-rise relative isolate pb-10 pt-[calc(30px+2rem)] sm:pb-16 sm:pt-[calc(30px+3.5rem)]" : "bb-rise relative isolate overflow-hidden rounded-[28px] px-6 py-6 ring-1 ring-bordeaux/10 sm:px-10 sm:py-10"}
      style={{ backgroundColor: "#E4E4D4", backgroundImage: `url(${PAPER_SMALL})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      {/* Em ecrãs maiores, o fundo vichy usa a versão grande */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 hidden sm:block" style={{ backgroundImage: `url(${PAPER})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <Frame>
      <div className="relative">
        <Spark className="bb-twinkle pointer-events-none absolute right-[22%] top-5 hidden h-3.5 w-3.5 md:block" style={{ animationDelay: ".6s" }} />
        <Spark className="bb-twinkle pointer-events-none absolute bottom-6 left-[46%] hidden h-2.5 w-2.5 md:block" style={{ animationDelay: "2s" }} />
        <Spark className="bb-twinkle pointer-events-none absolute right-5 top-4 h-3 w-3 md:right-[7%] md:top-auto md:bottom-8 md:h-3.5 md:w-3.5" style={{ animationDelay: "1.3s" }} />

        <div className="relative grid gap-x-10 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div className="relative min-w-0 rounded-[26px] bg-ivory/[0.74] p-5 shadow-[0_14px_36px_-20px_rgba(95,52,59,.4)] ring-1 ring-white/70 backdrop-blur-[3px] sm:p-7 md:p-9" data-testid="postcard-card">
            <span aria-hidden="true" className="pointer-events-none absolute inset-[6px] rounded-[21px] border border-dashed border-bordeaux/20" />
            <div className="flex items-start gap-3.5 sm:gap-4">
              <Bow className="mt-1 h-8 w-10 shrink-0 sm:h-9 sm:w-11 md:hidden" />
              <div className="min-w-0 flex-1 pr-7 md:pr-0">
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="bb-eyebrow">Agenda de Natal{year ? ` ${year}` : ""}</span>
                  {almost && <span className="rounded-full bg-rose-soft px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-bordeaux" data-testid="postcard-almost-pill">Últimas vagas</span>}
                </p>
                <h2 className="bb-h2 mt-1.5 max-w-[22ch] text-ink md:mt-3" style={{ fontSize: "clamp(1.45rem, 3.4vw, 2.5rem)" }}>
                  {closed ? <>A agenda está <span className="bb-accent text-bordeaux">cheia</span></> : <Title text={agenda.title} />}
                </h2>
                {/* Telemóvel, fechado: uma linha com o essencial */}
                {!closed && iso && !open && (
                  <Live iso={iso} offset={offset}>
                    {(t) => {
                      const [n, u] = biggest(t);
                      return <p className="mt-1.5 text-[13px] text-taupe-strong md:hidden" data-testid="postcard-summary">Até {dateLabel(iso, true)} · faltam <strong className="font-semibold text-ink">{n} {u}</strong></p>;
                    }}
                  </Live>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-controls={toggleId}
                aria-label={open ? "Fechar os detalhes da agenda de Natal" : "Ver os detalhes da agenda de Natal"}
                data-testid="postcard-toggle"
                className="absolute right-2 top-2 grid h-11 w-11 place-items-center rounded-full bg-rose-soft/70 text-bordeaux transition-colors hover:bg-rose-soft md:hidden"
              >
                <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
            </div>

            <div id={toggleId} className={`${open ? "block" : "hidden"} bb-rise md:block`} data-testid="postcard-details">
              <p className="mt-3 max-w-[52ch] text-[15px] leading-relaxed text-ink/85 md:mt-4 md:text-base" data-testid="postcard-message">{text}</p>
              {!closed && (
                <ul className="mt-4 space-y-2.5 md:mt-5" data-testid="postcard-facts">
                  {iso && <Fact icon={CalendarHeart}>Encomendas até <strong className="font-semibold">{untilText(iso)}</strong></Fact>}
                  {agenda.deliveryText && <Fact icon={PackageCheck}>Prazo estimado de produção e entrega: <strong className="font-semibold" data-testid="postcard-delivery">{agenda.deliveryText}</strong></Fact>}
                </ul>
              )}
              {!closed && iso && (
                <Live iso={iso} offset={offset}>
                  {(t) => (
                    <p className="mt-4 text-[13px] tabular-nums text-taupe-strong md:hidden" data-testid="postcard-clock">
                      Faltam <strong className="font-semibold text-ink">{t.days} {plural(t.days, "dia", "dias")}, {pad(t.hours)} h {pad(t.minutes)} min {pad(t.seconds)} s</strong>
                    </p>
                  )}
                </Live>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 md:mt-7">
                <Cta {...ctaProps} data-testid="postcard-cta" className={closed ? "bb-btn-ghost" : "bb-btn-primary"}>{closed ? "Espreitar as peças" : "Explorar os produtos"}</Cta>
                {!closed && <span className="text-[12.5px] leading-snug text-taupe-strong">Cada peça é feita à mão, por encomenda.</span>}
              </div>
            </div>
          </div>

          {/* Carimbo (só a partir de tablet): data de fecho e o que falta */}
          <div className="relative hidden md:mr-6 md:block lg:mr-14" data-testid="postcard-stamp">
            <Holly className="absolute -left-16 -top-9 h-[96px] w-[137px] -rotate-6" />
            <Holly className="absolute -bottom-8 -right-14 h-[84px] w-[120px] rotate-[8deg] -scale-x-100" />
            {closed ? (
              <Postmark size={188} ring={`AGENDA CHEIA DE AMOR  ✦  OBRIGADA  ✦  BABYBIB  ✦  `} big="♡" small="Obrigada" />
            ) : iso ? (
              <Live iso={iso} offset={offset}>
                {(t) => {
                  const [n, u] = biggest(t);
                  return (
                    <div className="flex flex-col items-center">
                      <Postmark size={188} ring={`ENCOMENDAS ATÉ ${dateLabel(iso, true).toUpperCase()} ${yearOf(iso)}  ✦  AGENDA DE NATAL  ✦  `} big={n} small={u} />
                      <p className="mt-3 rounded-full bg-ivory/80 px-3.5 py-1 text-[13px] tabular-nums text-taupe-strong ring-1 ring-white/70" data-testid="postcard-clock-desktop">
                        e {pad(t.days >= 1 ? t.hours : t.minutes)} {t.days >= 1 ? "h" : "min"} {pad(t.days >= 1 ? t.minutes : t.seconds)} {t.days >= 1 ? "min" : "s"}
                      </p>
                    </div>
                  );
                }}
              </Live>
            ) : (
              <Postmark size={188} ring={`AGENDA DE NATAL  ✦  FEITO À MÃO  ✦  BABYBIB  ✦  `} big="♡" small="Natal" />
            )}
          </div>
        </div>
      </div>
      </Frame>
    </section>
  );
}

// Fita cosida na junção com a secção seguinte.
function RibbonDivider() {
  return (
    <div aria-hidden="true" className="relative z-10 -mt-[11px] h-[22px]" data-testid="postcard-ribbon">
      <div className="bb-ribbon absolute inset-x-0 top-0 h-[22px]" />
    </div>
  );
}

/** Na homepage: só aparece se a Alexandra a ativou, e não quando a loja está suspensa (nesse caso fala o aviso da loja). */
export function ChristmasPostcard() {
  const agenda = useAgenda();
  // Com ?agenda=1 na morada, a Alexandra vê o postal mesmo escondido (só no ecrã dela, não muda nada para as clientes).
  const peek = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("agenda") === "1";
  if (!agenda || (!agenda.visible && !peek) || agenda.suspended) return null;
  return (
    <div className="-mt-[30px]" data-testid="home-christmas">
      <PostcardView band agenda={agenda} />
      <RibbonDivider />
    </div>
  );
}
